import React, { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Shield, 
  Briefcase,
  CreditCard,
  FileText,
  Building,
  Plus,
  Camera,
  Key
} from 'lucide-react';
import { collection, addDoc, serverTimestamp, getDocs, doc, updateDoc, setDoc } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { createEmployeeAccount } from '../../services/employeeService';

/**
 * Modal complet pour ajouter un nouvel intervenant
 */
const AddEmployeeModal = ({ db, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    birthDate: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'France'
    },
    role: '',
    hourlyRate: '',
    isAutoEntrepreneur: false,
    defaultRadius: 10,
    color: '#667eea',
    isActive: true,
    socialSecurity: '',
    bankInfo: {
      iban: '',
      bic: ''
    },
    professionalInfo: {
      contractType: '',
      startDate: '',
      endDate: '',
      weeklyHours: '',
      cesuNumber: '',
      nanCrcesu: ''
    },
    deplacementRate: {
      base: 2.50,
      includedDistance: 20,
      perKm: 0.40
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [createdEmployee, setCreatedEmployee] = useState(null);
  const [tempPassword, setTempPassword] = useState('');

  // Charger les rôles dynamiquement depuis Firestore
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
        const rolesData = rolesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Trier les rôles par niveau (du plus élevé au plus bas)
        const sortedRoles = rolesData.sort((a, b) => (b.level || 0) - (a.level || 0));
        setRoles(sortedRoles);
      } catch (error) {
        console.error('Erreur lors du chargement des rôles:', error);
        setError('Erreur lors du chargement des rôles');
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [db]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else if (name.startsWith('bankInfo.')) {
      const bankField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        bankInfo: {
          ...prev.bankInfo,
          [bankField]: value
        }
      }));
    } else if (name.startsWith('professionalInfo.')) {
      const professionalField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        professionalInfo: {
          ...prev.professionalInfo,
          [professionalField]: value
        }
      }));
    } else if (name.startsWith('deplacementRate.')) {
      const deplacementField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        deplacementRate: {
          ...prev.deplacementRate,
          [deplacementField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validation des champs requis
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        throw new Error('Le prénom et le nom sont obligatoires');
      }

      if (!formData.email.trim()) {
        throw new Error('L\'email est obligatoire');
      }

      // Validation de l'email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Format d\'email invalide');
      }

      // Générer un mot de passe temporaire (comme dans l'ancien système)
      const generateTemporaryPassword = () => {
        const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
        let password = '';
        for (let i = 0; i < 12; i++) {
          password += charset.charAt(Math.floor(Math.random() * charset.length));
        }
        return password;
      };

      const tempPassword = generateTemporaryPassword();

      // Préparer les données pour Firestore
      const employeeData = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phoneNumber: formData.phoneNumber?.trim() || '',
        hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : null,
        defaultRadius: parseInt(formData.defaultRadius) || 10,
        tempPassword: tempPassword,
        needPasswordReset: true,
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp()
      };

      console.log('🔐 Création du compte employé via service...');
      console.log('📊 Données préparées pour Firestore:', JSON.stringify(employeeData, null, 2));
      
      // Utiliser le service pour créer le compte via la fonction Cloud
      const result = await createEmployeeAccount(employeeData, tempPassword);
      
      const userId = result.userId;
      console.log('✅ Compte employé créé avec succès:', userId);

      // Stocker les informations de l'intervenant créé
      const createdEmployeeData = {
        id: userId, // Utiliser l'UID Firebase comme ID
        ...employeeData,
        authUid: userId,
        userId: userId
      };
      
      setCreatedEmployee(createdEmployeeData);
      setTempPassword(tempPassword);
      
      if (onSuccess) {
        onSuccess(createdEmployeeData);
      }

    } catch (error) {
      console.error('❌ Erreur lors de la création de l\'intervenant:', error);
      setError(error.message || 'Une erreur est survenue lors de la création');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-opacity-70">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* En-tête du modal */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Nouvel intervenant</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Ajouter un nouvel intervenant au système</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Corps du modal */}
        <div className="p-6">
          {createdEmployee ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Intervenant créé avec succès !
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                L'intervenant a été créé dans Firestore et un compte Firebase Auth a été créé avec un mot de passe temporaire.
              </p>
              
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6 text-left">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Informations du compte :</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Nom :</span>
                    <span className="font-medium text-gray-900 dark:text-white">{createdEmployee.firstName} {createdEmployee.lastName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Email :</span>
                    <span className="font-medium text-gray-900 dark:text-white">{createdEmployee.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Rôle :</span>
                    <span className="font-medium text-gray-900 dark:text-white">{createdEmployee.role || 'Non défini'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">UID Firebase :</span>
                    <span className="font-medium text-xs text-gray-900 dark:text-white">{createdEmployee.authUid}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2 flex items-center">
                  <Key className="w-4 h-4 mr-2" />
                  Mot de passe temporaire
                </h4>
                <div className="bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded p-3 mb-3">
                  <code className="text-lg font-mono text-blue-700 dark:text-blue-300">{tempPassword}</code>
                </div>
                <p className="text-blue-700 dark:text-blue-300 text-sm">
                  Ce mot de passe a été stocké dans Firestore et doit être communiqué à l'intervenant.
                  Il devra le changer lors de sa première connexion.
                </p>
              </div>
              
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Fermer
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                  <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
                </div>
              )}

              {/* Informations principales */}
              <div className="mb-6">
                <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  <User size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
                  Informations principales
                </h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Prénom *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        placeholder="Prénom"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                        placeholder="Nom"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      placeholder="email@exemple.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+33 1 23 45 67 89"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rôle</label>
                    {loadingRoles ? (
                      <div className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-500">Chargement des rôles...</div>
                    ) : (
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="utilisateur">Sélectionner un rôle</option>
                        {roles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {/* Adresse */}
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  <MapPin size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
                  Adresse
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rue</label>
                    <input
                      type="text"
                      name="address.street"
                      value={formData.address.street}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Numéro et nom de rue"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code postal</label>
                    <input
                      type="text"
                      name="address.postalCode"
                      value={formData.address.postalCode}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="75000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ville</label>
                    <input
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Paris"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pays</label>
                    <input
                      type="text"
                      name="address.country"
                      value={formData.address.country}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="France"
                    />
                  </div>
                </div>
              </div>

              {/* Informations professionnelles */}
              <div className="mb-6">
                <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  <Building size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
                  Informations professionnelles
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de contrat *</label>
                    <select
                      name="professionalInfo.contractType"
                      value={formData.professionalInfo.contractType}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Sélectionner un type de contrat</option>
                      <option value="Indépendant">Indépendant</option>
                      <option value="Cesu">Cesu</option>
                      <option value="CDI">CDI</option>
                      <option value="CDD">CDD</option>
                      <option value="Interim">Intérim</option>
                      <option value="Stage">Stage</option>
                    </select>
                  </div>
                  {formData.professionalInfo.contractType === 'Cesu' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Numéro CESU</label>
                        <input
                          type="text"
                          name="professionalInfo.cesuNumber"
                          value={formData.professionalInfo.cesuNumber}
                          onChange={handleChange}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Ex: 123456789"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code NAN CRCESU</label>
                        <input
                          type="text"
                          name="professionalInfo.nanCrcesu"
                          value={formData.professionalInfo.nanCrcesu}
                          onChange={handleChange}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Ex: SAP123456789"
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de début *</label>
                    <input
                      type="date"
                      name="professionalInfo.startDate"
                      value={formData.professionalInfo.startDate}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de fin</label>
                    <input
                      type="date"
                      name="professionalInfo.endDate"
                      value={formData.professionalInfo.endDate}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Heures hebdomadaires</label>
                    <input
                      type="number"
                      name="professionalInfo.weeklyHours"
                      value={formData.professionalInfo.weeklyHours}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      max="60"
                      step="0.5"
                      placeholder="35"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Zone d'intervention</label>
                    <select
                      name="defaultRadius"
                      value={formData.defaultRadius}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={5}>5 km</option>
                      <option value={10}>10 km</option>
                      <option value={15}>15 km</option>
                      <option value={20}>20 km</option>
                      <option value={30}>30 km</option>
                      <option value={50}>50 km</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Tarification */}
              <div className="mb-6 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  <DollarSign size={16} className="mr-2 text-orange-600 dark:text-orange-400" />
                  Tarification
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Taux horaire (€)</label>
                    <input
                      type="number"
                      name="hourlyRate"
                      value={formData.hourlyRate}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="isAutoEntrepreneur"
                      checked={formData.isAutoEntrepreneur}
                      onChange={handleChange}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 mr-2"
                    />
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Briefcase size={16} className="inline mr-1" />
                      Auto-entrepreneur
                    </label>
                  </div>
                </div>

                {/* Tarifs de déplacement pour auto-entrepreneurs */}
                {formData.isAutoEntrepreneur && (
                  <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <h5 className="font-medium text-yellow-800 dark:text-yellow-300 mb-3">Tarifs de déplacement</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Forfait base (€)</label>
                        <input
                          type="number"
                          name="deplacementRate.base"
                          value={formData.deplacementRate.base}
                          onChange={handleChange}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Distance incluse (km)</label>
                        <input
                          type="number"
                          name="deplacementRate.includedDistance"
                          value={formData.deplacementRate.includedDistance}
                          onChange={handleChange}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                          step="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tarif/km (€)</label>
                        <input
                          type="number"
                          name="deplacementRate.perKm"
                          value={formData.deplacementRate.perKm}
                          onChange={handleChange}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Informations bancaires */}
              <div className="mb-6">
                <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  <CreditCard size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
                  Informations bancaires
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IBAN</label>
                    <input
                      type="text"
                      name="bankInfo.iban"
                      value={formData.bankInfo.iban}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">BIC</label>
                    <input
                      type="text"
                      name="bankInfo.bic"
                      value={formData.bankInfo.bic}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="ABCDEFGH"
                    />
                  </div>
                </div>
              </div>

              {/* Sécurité sociale */}
              <div className="mb-6">
                <h4 className="flex items-center text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  <Shield size={16} className="mr-2 text-blue-600 dark:text-blue-400" />
                  Sécurité sociale
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Numéro de sécurité sociale</label>
                    <input
                      type="text"
                      name="socialSecurity"
                      value={formData.socialSecurity}
                      onChange={handleChange}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="X XX XX XX XXX XXX XX"
                      maxLength="15"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  disabled={isSubmitting}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting || !formData.firstName || !formData.lastName || !formData.email || !formData.professionalInfo.contractType || !formData.professionalInfo.startDate}
                >
                  <Plus size={16} className="mr-2" />
                  {isSubmitting ? 'Création...' : 'Créer l\'intervenant'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddEmployeeModal;
